/**
 * Billing Portal Hook
 *
 * Opens Stripe's billing portal in a new window for managing subscription,
 * payment methods, and invoices.
 */

import { useCallback } from 'react';
import { useCurrentUser } from './use-current-user';
import { useCreateBillingPortalSessionMutation, BillingPortalFlowDataInput } from '@/lib/graphql/types-and-hooks';
import { useWorkspaceId } from './use-workspace-id';

interface OpenBillingPortalOptions {
  flowData?: BillingPortalFlowDataInput;
}

export function useBillingPortal() {
  const { currentUser } = useCurrentUser();
  const workspaceId = useWorkspaceId();
  const [createBillingPortalSession] = useCreateBillingPortalSessionMutation();

  const openBillingPortal = useCallback(async (options?: OpenBillingPortalOptions) => {
    if (!workspaceId) {
      alert('No workspace found. Please try again.');
      return;
    }

    // Open a new window synchronously (required for Safari popup blocker)
    const newWindow = window.open('about:blank', '_blank');
    if (!newWindow) {
      alert('Please allow popups for this site to open the billing portal.');
      return;
    }

    // Write a loading page to avoid the blank state
    newWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Loading...</title>
          <style>
            body {
              margin: 0;
              padding: 0;
              display: flex;
              align-items: center;
              justify-content: center;
              height: 100vh;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
              background: #f5f5f5;
            }
            .loader {
              text-align: center;
            }
            .spinner {
              border: 3px solid #f3f3f3;
              border-top: 3px solid #333;
              border-radius: 50%;
              width: 40px;
              height: 40px;
              animation: spin 1s linear infinite;
              margin: 0 auto 16px;
            }
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            .text {
              color: #666;
              font-size: 14px;
            }
          </style>
        </head>
        <body>
          <div class="loader">
            <div class="spinner"></div>
            <div class="text">Opening billing portal...</div>
          </div>
        </body>
      </html>
    `);
    newWindow.document.close();

    try {
      const result = await createBillingPortalSession({
        variables: {
          input: {
            workspaceId,
            returnUrl: `${window.location.origin}/stripe/callback`,
            ...(options?.flowData && { flowData: options.flowData }),
          },
        },
      });

      const portalUrl = result.data?.createBillingPortalSession?.url;
      if (portalUrl) {
        // Redirect the new window to Stripe's hosted billing portal
        newWindow.location.href = portalUrl;
      } else {
        console.error('No portal URL returned from backend');
        newWindow.close();
        alert('Failed to create billing portal session. Please try again.');
      }
    } catch (error) {
      console.error('Error creating billing portal session:', error);
      newWindow.close();
      alert('Failed to open billing portal. Please try again or contact support.');
    }
  }, [workspaceId, createBillingPortalSession]);

  return { openBillingPortal };
}
